import { Buttonv2 } from "@itandi/itandi-bb-ui/Buttonv2";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { InputText } from "@itandi/itandi-bb-ui/InputText";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { useContext, useState } from "react";
import useSWRMutation from "swr/mutation";
import { useDealContext } from "~/context/DealContext";
import { useDealWorkflowRestriction } from "~/hooks/deals/useDealWorkflowRestriction";
import { dealCacheKey } from "~/hooks/useDeals";
import { getOriginVendor } from "~/models/DealVendorCompany";
import { ItandiAccountContext } from "~/models/ItandiAccount";
import { hasPermissionToOperateDeal } from "~/models/Staff";
import { isSuccess } from "~/utils/api/api_handler";
import { create as postCancel } from "~/utils/api/deals/DealCancel";

export function DealCancelButtonClient() {
	const { notify } = useNotification();
	const deal = useDealContext();
	const account = useContext(ItandiAccountContext);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [reason, setReason] = useState("");
	const assignedVendor = getOriginVendor(deal);
	const {
		data: workflowRestriction,
		error: workflowRestrictionError,
		isLoading: isWorkflowRestrictionLoading
	} = useDealWorkflowRestriction(deal.id);

	const { trigger, isMutating } = useSWRMutation(
		dealCacheKey(deal.id),
		async () => postCancel(deal.id, reason)
	);

	const handleAccept = async (): Promise<void> => {
		const result = await trigger();
		if (isSuccess(result)) {
			notify({ content: "案件を取り下げました。", variant: "success" });
			setIsModalOpen(false);
		} else {
			notify({
				content: result.message ?? "エラーが発生しました。",
				variant: "error"
			});
		}
	};

	return (
		<>
			<Buttonv2
				color="danger"
				disabled={
					!hasPermissionToOperateDeal(account) ||
					isWorkflowRestrictionLoading ||
					!!workflowRestrictionError ||
					!workflowRestriction?.cancelable
				}
				ml="auto"
				onClick={() => {
					setIsModalOpen(true);
				}}
				variant="secondary"
			>
				案件の取り下げ
			</Buttonv2>
			{isModalOpen ? (
				<Modalv2
					isOpen
					onClose={() => {
						setIsModalOpen(false);
					}}
				>
					<ModalHeader title="案件の取り下げ" />
					<ModalBody>
						<FormControl
							label={`取り下げ理由${
								assignedVendor ? "（施工会社に通知されます）" : ""
							}`}
							variant="vertical"
						>
							<InputText
								maxRows={8}
								onChange={(e) => {
									setReason(e.target.value);
								}}
								resize="autoRows"
								rows={4}
								value={reason}
							/>
						</FormControl>
					</ModalBody>
					<ModalFooter
						leftArea={
							<Buttonv2
								disabled={isMutating}
								onClick={() => {
									setIsModalOpen(false);
								}}
								variant="secondary"
							>
								キャンセル
							</Buttonv2>
						}
						rightArea={
							<Buttonv2
								disabled={!!assignedVendor && reason === ""}
								loading={isMutating}
								onClick={handleAccept}
								variant="primary"
							>
								取り下げ
							</Buttonv2>
						}
					/>
				</Modalv2>
			) : null}
		</>
	);
}
